import React, { useEffect, useState } from "react";
import styled from "styled-components";
import sortBy from "lodash/sortBy";
import { Trans, useTranslation } from "react-i18next";
import useProductName from "brand-customization/useProductName";
import useResourceCustomization from "brand-customization/useResourceCustomization";
import { useConfiguration } from "forwarder/wizard/select-forwarder-step/hooks/useConfiguration";
import { Icon } from "components/common";
import { Col, Panel, PanelGroup, Row } from "components/bootstrap";
import AppConfig from "util/AppConfig";
import { fetchInputs } from "hooks/useInputs";
import { StyledPanel } from "forwarder/CommonStyledComponents";
import useBasicForwarderUser from "forwarder/wizard/select-forwarder-step/hooks/useBasicForwarderUser";
import LinkToAPIToken from "./LinkToAPIToken";
import InstallForwarderStep from "./start-new-forwarder-steps/InstallForwarderStep";
import CreateTokenStep from "./start-new-forwarder-steps/CreateTokenStep";
import ConfigureNewForwarderStep from "./start-new-forwarder-steps/ConfigureNewForwarderStep";
import SelectNewForwarderStep from "./start-new-forwarder-steps/SelectNewForwarderStep";
import { StyledTitle } from "../StyledWizardComponents";
const StyledPanelGroup = styled(PanelGroup)`
  &.panel-group > .panel {
    margin-top: 0;
    border-color: ${(props) => props.theme.colors.input.border};
    background-color: ${(props) => props.theme.colors.global.contentBackground};

    .panel-heading {
      background-color: ${(props) => props.theme.colors.table.row.backgroundStriped};
    }

    &:not(:first-child) {
      border-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    &:not(:last-child) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;
const StyledUL = styled.ul`
  list-style-type: disc;
  margin: 10px 0;
`;
const StyledHelpPanel = styled(StyledPanel)`
  margin-top: 30px;
`;
const CLOUD_FORWARDER_CONFIGURATION_PORT = "13301";
const CLOUD_FORWARDER_TRANSMISSION_PORT = "13302";
const FirewallHelpMessage = ({ configuration }) => {
  const { t } = useTranslation("enterprise_forwarder");
  const productName = useProductName();
  if (AppConfig.isCloud()) {
    return /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "firewall_help.ports",
        values: {
          productName,
          configurationPort: CLOUD_FORWARDER_CONFIGURATION_PORT,
          transmissionPort: CLOUD_FORWARDER_TRANSMISSION_PORT
        },
        components: { code: /* @__PURE__ */ React.createElement("code", null) }
      }
    ));
  }
  if (configuration.forwarder_configuration_port && configuration.forwarder_message_transmission_port) {
    return /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "firewall_help.ports",
        values: {
          productName,
          configurationPort: configuration.forwarder_configuration_port,
          transmissionPort: configuration.forwarder_message_transmission_port
        },
        components: { code: /* @__PURE__ */ React.createElement("code", null) }
      }
    ));
  }
  return /* @__PURE__ */ React.createElement("li", null, t("firewall_help.default", { productName }));
};
const StartNewForwarder = ({ forwarders = [], onForwarderSelect }) => {
  const { t } = useTranslation("enterprise_forwarder");
  const { data: forwarderUser } = useBasicForwarderUser();
  const productName = useProductName();
  const [activeStep, setActiveStep] = useState("installForwarder");
  const [forwarderInputs, setForwarderInputs] = useState();
  const [configuration] = useConfiguration(forwarderInputs == null ? void 0 : forwarderInputs[0]);
  const [token, setToken] = useState();
  const { enabled, url } = useResourceCustomization("contact_us");
  useEffect(() => {
    if (!AppConfig.isCloud()) {
      fetchInputs().then(({ inputs }) => {
        const sortedForwarderInputs = sortBy(
          inputs.filter((input) => input.type === "org.graylog.plugins.forwarder.input.ForwarderServiceInput"),
          "created_at"
        );
        setForwarderInputs(sortedForwarderInputs);
      });
    }
  }, []);
  const handleTokenCreation = (nextToken) => {
    setToken(nextToken);
    setActiveStep("configureForwarder");
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(StyledTitle, null, t("start_new.heading")), /* @__PURE__ */ React.createElement("p", null, t("start_new.intro", { productName })), /* @__PURE__ */ React.createElement(
    StyledPanelGroup,
    {
      accordion: true,
      id: "startForwarder",
      activeKey: activeStep,
      onSelect: setActiveStep
    },
    /* @__PURE__ */ React.createElement(InstallForwarderStep, { eventKey: "installForwarder", onStepComplete: () => setActiveStep("createToken") }),
    /* @__PURE__ */ React.createElement(
      CreateTokenStep,
      {
        eventKey: "createToken",
        user: forwarderUser,
        forwarderInputs,
        token,
        onTokenCreate: handleTokenCreation,
        onStepComplete: () => setActiveStep("configureForwarder")
      }
    ),
    /* @__PURE__ */ React.createElement(
      ConfigureNewForwarderStep,
      {
        eventKey: "configureForwarder",
        token,
        forwarderInputs,
        onStepComplete: () => setActiveStep("selectForwarder")
      }
    ),
    /* @__PURE__ */ React.createElement(
      SelectNewForwarderStep,
      {
        eventKey: "selectForwarder",
        forwarders,
        onForwarderSelect
      }
    )
  )), /* @__PURE__ */ React.createElement(Col, { md: 5, mdOffset: 1 }, /* @__PURE__ */ React.createElement(StyledHelpPanel, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Panel.Heading, null, /* @__PURE__ */ React.createElement(Panel.Title, { componentClass: "h3" }, /* @__PURE__ */ React.createElement(Icon, { name: "info" }), " ", t("start_new.help_heading"))), /* @__PURE__ */ React.createElement(Panel.Body, null, /* @__PURE__ */ React.createElement("p", null, t("start_new.trouble_intro", { productName })), /* @__PURE__ */ React.createElement(StyledUL, null, /* @__PURE__ */ React.createElement("li", null, t("start_new.check_url", { productName })), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "start_new.check_token",
      components: {
        tokenLink: /* @__PURE__ */ React.createElement(LinkToAPIToken, { user: forwarderUser }),
        code: /* @__PURE__ */ React.createElement("code", null)
      }
    }
  )), /* @__PURE__ */ React.createElement(FirewallHelpMessage, { configuration })), enabled && /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "start_new.still_trouble",
      components: {
        contactLink: /* @__PURE__ */ React.createElement("a", { href: url, target: "_blank", rel: "noopener noreferrer" }, t("start_new.contact_us"))
      }
    }
  ))))));
};
export default StartNewForwarder;
